import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { capitalize } from "lodash-es";
import { formatNumber } from "@SeemplicityDev/shared";

const MIN_LENGTH = 10;
const MEDIUM_MIN_LENGTH = 15;
const MIN_VAL = 3;
const MEDIUM_MIN_VAL = 10;

export type ChartDataValue = number | null | undefined;

export type ChartDataPoint = Record<string, string | ChartDataValue>;

export const getDataVal = (
  val: number | undefined,
  total: number,
  max: number,
  width: number
): ChartDataValue => {
  if (!val && total === 0) {
    return val ?? undefined;
  }

  if (val === 0) {
    return null;
  }

  if (!!width) {
    const pixelsPerEntity = width / max;
    const valPixels = val! * pixelsPerEntity;

    const entityPerPixel = max / width;
    const minEntitiesPerPixel = MIN_LENGTH * entityPerPixel;
    const mediumMinEntitiesPerPixel = MEDIUM_MIN_LENGTH * entityPerPixel;

    if (valPixels < MIN_VAL) {
      return minEntitiesPerPixel;
    }
    if (valPixels < MEDIUM_MIN_VAL) {
      return mediumMinEntitiesPerPixel;
    }
  }

  return val ?? undefined;
};

export const getAdjustedDataVals = (
  values: (number | undefined)[],
  total: number,
  max: number,
  width: number
): ChartDataValue[] => {
  const adjusted = values.map((val) => getDataVal(val, total, max, width));
  const adjustedSum = adjusted.reduce<number>((sum, v) => sum + (v || 0), 0);

  if (adjustedSum <= max || adjustedSum === 0) {
    return adjusted;
  }

  const scale = max / adjustedSum;
  return adjusted.map((v) => (v != null && v > 0 ? v * scale : v));
};

export const buildChartData = (
  keys: string[],
  values: (number | undefined)[],
  total: number,
  max: number,
  width: number,
  baseFields: Record<string, string>
): ChartDataPoint => {
  const adjustedValues = getAdjustedDataVals(values, total, max, width);
  return keys.reduce<ChartDataPoint>(
    (acc, key, i) => {
      acc[key] = values[i];
      acc[`${key}Val`] = adjustedValues[i];
      return acc;
    },
    { ...baseFields }
  );
};

export const createValueAxis = (
  valueAxis: am4charts.ValueAxis,
  maxValue: number
): void => {
  valueAxis.min = 0;
  valueAxis.max = maxValue * 1.1;
  valueAxis.strictMinMax = true;
  valueAxis.layout = "absolute";
  valueAxis.renderer.baseGrid.disabled = true;
  valueAxis.renderer.disabled = true;
  valueAxis.renderer.grid.template.disabled = true;
  valueAxis.renderer.labels.template.disabled = true;
  valueAxis.renderer.labels.template.height = 0;
  valueAxis.renderer.ticks.template.disabled = true;
};

export const createCategoryAxis = (
  categoryAxis: am4charts.CategoryAxis,
  category: string
): void => {
  categoryAxis.dataFields.category = category;
  categoryAxis.renderer.disabled = true;
  categoryAxis.renderer.grid.template.disabled = true;
  categoryAxis.renderer.labels.template.disabled = true;
  categoryAxis.renderer.labels.template.height = 0;
  categoryAxis.renderer.ticks.template.disabled = true;
};

export const createSeries = (
  field: string,
  name: string,
  value: number,
  fillColor: string,
  category: string,
  isExported: boolean,
  isClickable?: boolean
): am4charts.ColumnSeries => {
  let series = new am4charts.ColumnSeries();
  series.name = name;
  series.dataFields.valueX = field;
  series.dataFields.categoryY = category;
  series.sequencedInterpolation = true;
  series.fill = am4core.color(fillColor);
  series.stacked = !isExported;
  series.tooltip!.autoTextColor = false;
  // amCharts coerces this string to a Color at runtime; preserved as-is.
  series.tooltip!.label.fill = "white" as unknown as am4core.Color;
  series.columns.template.strokeWidth = isExported ? 2 : 4;
  series.columns.template.height = isExported ? 6 : 16;
  series.columns.template.column.cornerRadiusTopLeft = 12;
  series.columns.template.column.cornerRadiusTopRight = 12;
  series.columns.template.column.cornerRadiusBottomLeft = 12;
  series.columns.template.column.cornerRadiusBottomRight = 12;
  series.cursorOverStyle = isClickable
    ? am4core.MouseCursorStyle.pointer
    : am4core.MouseCursorStyle.default;
  series.columns.template.tooltipHTML = `<div style="padding: 0 0 4px; font-size: 14px; font-weight: 500;">${capitalize(
    name
  )}:<span style="font-weight: 400;"> ${formatNumber(value)}</span></div>`;

  if (isExported) {
    const labelBullet = series.bullets.push(new am4charts.LabelBullet());
    labelBullet.fontSize = 10;
    labelBullet.dx = 25;
    labelBullet.label.text = `${formatNumber(value)}`;
    labelBullet.label.truncate = false;
    labelBullet.label.hideOversized = false;
    labelBullet.label.fontWeight = "600";
  }

  return series;
};
